import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSocket } from '../../contexts/SocketContext'
import { useAudioAlerts } from '../../hooks/useAudioAlerts'
import { useAppMode } from '../../contexts/AppModeContext'

interface Invite { chatId: string; invitedBy: string; label: string }

/**
 * App-wide listener for whisper invitations. When the agent handling a chat
 * invites this user, the server pushes `whisper_invited` to their agent room;
 * we show a prominent top banner + play a tune. "Accept" opens the chat — where
 * they're already an observer, so it loads straight into whisper mode.
 */
export const GlobalWhisperInvites: React.FC = () => {
  const { socket } = useSocket()
  const { mode } = useAppMode()
  const navigate = useNavigate()
  // Whisper invites use the short "ding" (the message tone), not the queue chime.
  const { playMessageAlert } = useAudioAlerts()
  const [invites, setInvites] = useState<Invite[]>([])

  useEffect(() => {
    if (!socket) return
    const onInvite = (data: any) => {
      const chatId = String(data?.chat_id ?? data?.chatId ?? '')
      if (!chatId) return
      setInvites((prev) => prev.some((i) => i.chatId === chatId) ? prev : [
        ...prev,
        { chatId, invitedBy: data?.invited_by || 'A colleague', label: data?.label || 'a live chat' },
      ])
      try { playMessageAlert() } catch { /* audio best-effort */ }
    }
    socket.on('whisper_invited', onInvite)
    return () => { socket.off('whisper_invited', onInvite) }
  }, [socket, playMessageAlert])

  const dismiss = (chatId: string) => setInvites((p) => p.filter((i) => i.chatId !== chatId))
  const accept = (inv: Invite) => { dismiss(inv.chatId); navigate(`/queue/${inv.chatId}`) }

  // Defensive: never surface whisper invites while in Dev Mode (audio is also
  // hard-gated in useAudioAlerts). All hooks above run unconditionally.
  if (mode === 'dev') return null

  if (invites.length === 0) return null

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] w-[min(94vw,460px)] space-y-2">
      {invites.map((inv) => (
        <div key={inv.chatId} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500 text-white shadow-xl animate-slide-down">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/20 shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight">{inv.invitedBy} invited you to whisper</p>
            <p className="text-xs text-white/90 truncate">on {inv.label}</p>
          </div>
          <button
            onClick={() => accept(inv)}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-white text-amber-700 text-sm font-semibold hover:bg-amber-50 transition-colors"
          >
            Accept invitation
          </button>
          <button onClick={() => dismiss(inv.chatId)} title="Dismiss" className="shrink-0 p-1 rounded-md text-white/80 hover:text-white hover:bg-white/15 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
    </div>
  )
}
